import { useMemo } from "react";
import { Contract, ContractDeviationType, CurrentCompany, DeviationProduct, InspectionFraction } from "@/types";
import { InspectionContext, InspectionContract, InspectionDeviationProduct, InspectionDeviationType } from "./types";
import { useAuth } from "@/contexts/AuthContext";
import { useGetOrderById } from "@/services/Orders";
import { useGetContractById } from "@/services/Contracts";
import { useGetContractDeviationTypesByIds } from "@/services/ContractDeviations";
import { useGetDeviationProducts } from "@/services/DeviationProducts";
import { useGetInspectionFractions, useGetOrderInspection } from "@/services/Inspections";
import { getAgreedPriceForCompany } from "@/utils/Contracts";
import DeviationPriceUnitEnum from "@/constants/enums/DeviationPriceUnitEnum";

// One array each for "no fractions" and "no products", so a failed call doesn't rebuild the context on every render.
const NO_FRACTIONS: InspectionFraction[] = [];
const NO_PRODUCTS: DeviationProduct[] = [];

/**
 * The contract's deviations with their type's name. A type that's disabled, or couldn't be loaded, isn't
 * offered: the backend refuses disabled types, and without the type there's no name to show.
 */
const getDeviationTypes = (
  contract: Contract | undefined,
  types: Map<number, ContractDeviationType>
): InspectionDeviationType[] =>
  (contract?.contractDeviations ?? []).flatMap((deviation) => {
    const type = types.get(deviation.contractDeviationTypeId);
    return type && !type.disabled
      ? [
          {
            id: type.id,
            name: type.name,
            price: deviation.price,
            currency: deviation.currency,
            unitCode: deviation.priceUnit.code,
          },
        ]
      : [];
  });

/** The active products, with their unit as a code like the services have (PK or PT). */
const getDeviationProducts = (products: DeviationProduct[]): InspectionDeviationProduct[] =>
  products
    .filter((product) => !product.disabled)
    .map((product) => ({
      id: product.id,
      name: product.name,
      price: product.price,
      currency: product.currency,
      unitCode:
        product.priceUnit === DeviationPriceUnitEnum.PT.id
          ? DeviationPriceUnitEnum.PT.code
          : DeviationPriceUnitEnum.PK.code,
    }));

/**
 * A contract an extra fraction can go on, priced from the fractions list. The inspector is always the buyer, so
 * it's the buyer's price.
 */
const fractionToContract = ({
  contractId,
  contractName,
  salesItemName,
  contractProduct,
}: InspectionFraction): InspectionContract => ({
  contractId,
  contractName,
  productName: salesItemName ?? contractProduct.companySpecificProduct.companySpecificProductName,
  price: contractProduct.agreedBuyerPricePrUnit[contractProduct.currencyType] ?? null,
  priceUnit: contractProduct.priceUnit,
  currency: contractProduct.currencyType,
  product: contractProduct.companySpecificProduct,
});

const toInspectionContract = (
  base: Pick<InspectionContract, "contractId" | "contractName" | "productName">,
  contract: Contract | undefined,
  company: CurrentCompany | undefined
): InspectionContract => ({
  ...base,
  price: contract ? getAgreedPriceForCompany(contract, company) : null,
  priceUnit: contract?.contractProduct.priceUnit ?? null,
  currency: contract?.contractProduct.currencyType ?? company?.companyCurrency ?? "NOK",
  product: contract?.contractProduct.companySpecificProduct ?? null,
});

/**
 * Everything the registration modal needs about one order: the order and its contract, the weight prefill,
 * the contracts extra streams can go on, each with its price from the inspecting company's side, the deviation
 * services on the order's contract and the inspecting company's deviation products.
 */
export const useInspectionContext = (orderId: number) => {
  const { currentCompany, user } = useAuth();
  const languageId = user?.language.id;

  const order = useGetOrderById(orderId, currentCompany?.companyId, languageId);
  const contractId = order.data?.contractId ?? null;
  const orderContract = useGetContractById(contractId as number, { enabled: !!contractId });
  const inspection = useGetOrderInspection(orderId);
  const fractions = useGetInspectionFractions(orderId);
  // Extra fractions are optional: when they can't be loaded, the order can still be inspected without them.
  const fractionList = fractions.isError ? NO_FRACTIONS : fractions.data;

  // A contract's deviation types belong to whoever set the contract up, so they're looked up by id.
  const typeQueries = useGetContractDeviationTypesByIds(
    orderContract.data?.contractDeviations.map((deviation) => deviation.contractDeviationTypeId) ?? []
  );
  const typesLoading = typeQueries.some((query) => query.isPending);
  const typesKey = typeQueries.map((query) => query.dataUpdatedAt).join();
  // Only the receiving company inspects, so its own products are the ones it can register.
  const products = useGetDeviationProducts(currentCompany?.companyId as number, false, {
    enabled: !!currentCompany?.companyId,
  });
  // Like the fractions: when the products can't be loaded, the order can still be inspected without them.
  const productList = products.isError ? NO_PRODUCTS : products.data;

  const context = useMemo<InspectionContext | null>(() => {
    if (!order.data || !inspection.data || !fractionList || !productList) return null;
    // Wait for the order's contract and its types, so the deviation options don't change under the inspector.
    if ((contractId && orderContract.isPending) || typesLoading) return null;

    const types = new Map(typeQueries.flatMap((query) => (query.data ? [[query.data.id, query.data] as const] : [])));

    const salesItem = order.data.salesItem;
    const productName =
      (languageId ? salesItem?.nameTranslated?.[languageId] : undefined) ??
      salesItem?.salesItem ??
      order.data.companySpecificProductDto?.companySpecificProductName ??
      "";

    return {
      order: {
        id: order.data.id,
        amount: order.data.amount,
        unit: order.data.unit,
        productName,
        contract: contractId
          ? toInspectionContract(
              { contractId, contractName: orderContract.data?.name ?? "", productName },
              orderContract.data,
              currentCompany
            )
          : null,
      },
      prefillWeightKg: inspection.data.prefillWeightKg,
      eligibleContracts: fractionList.map(fractionToContract),
      deviationTypes: getDeviationTypes(orderContract.data, types),
      deviationProducts: getDeviationProducts(productList),
    };
    // useQueries returns a new array every render, so the queries are tracked by when their data last changed.
  }, [
    order.data,
    orderContract.data,
    orderContract.isPending,
    inspection.data,
    fractionList,
    typesKey,
    typesLoading,
    productList,
    currentCompany,
    languageId,
  ]);

  return {
    context,
    status: order.data?.status,
    /** Undefined until the inspection has loaded. */
    inspections: inspection.data?.inspections,
    isError: order.isError || inspection.isError,
  };
};
